import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  Chip,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useDataProvider, useNotify, Button } from 'react-admin';
import { format } from 'date-fns';
import { IncidentDialog } from './IncidentDialog';

interface IncidentShowDialogProps {
  open: boolean;
  onClose: () => void;
  recordId: number;
  equipmentId: number;
}

const formatDate = (dateString: string) => {
  if (!dateString) return 'Not specified';
  return format(new Date(dateString), 'PPP');
};

const formatCurrency = (amount?: number) => {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatFileSize = (bytes?: number) => {
  if (!bytes) return 'Unknown size';
  const mb = bytes / (1024 * 1024);
  return mb < 1 ? `${(bytes / 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`;
};

const incidentTypeLabels: Record<string, string> = {
  ACCIDENT: 'Accident',
  BREAKDOWN: 'Breakdown',
  THEFT: 'Theft',
  DAMAGE: 'Damage',
  VANDALISM: 'Vandalism',
  MAINTENANCE_ISSUE: 'Maintenance Issue',
  SAFETY_ISSUE: 'Safety Issue',
  COLLISION: 'Collision',
  FIRE: 'Fire',
  FLOOD: 'Flood',
  OTHER: 'Other'
};

export const IncidentShowDialog: React.FC<IncidentShowDialogProps> = ({
  open,
  onClose,
  recordId,
  equipmentId,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [record, setRecord] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [files, setFiles] = useState<any[]>([]);

  useEffect(() => {
    if (open && recordId) {
      loadRecord();
    }
  }, [open, recordId]);

  const loadRecord = async () => {
    setLoading(true);
    try {
      const { data } = await dataProvider.getOne('equipment-incidents', { id: recordId });
      setRecord(data);
      setFiles(data.files || []);
    } catch (error) {
      console.error('Error loading incident record:', error);
      notify('Error loading incident record', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    loadRecord(); // Reload to get updated data
  };

  const handleViewImage = (file: any) => {
    if (file.file?.url) {
      window.open(file.file.url, '_blank');
    }
  };

  const handleDownloadFile = (file: any) => {
    if (file.file?.url) {
      window.open(file.file.url, '_blank');
    }
  };

  if (!record) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
          },
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Incident Details
          </Typography>
          <Box>
            <IconButton onClick={handleEdit} color="primary" sx={{ mr: 1 }}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {/* Main Information */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Incident Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CalendarTodayIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Incident Date</Typography>
                    <Typography variant="body1">
                      {formatDate(record.incidentDate)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <WarningIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Incident Type</Typography>
                    <Typography variant="body1">
                      {incidentTypeLabels[record.incidentType] || record.incidentType || 'Not specified'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              {record.location && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationOnIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Location</Typography>
                      <Typography variant="body1">
                        {record.location}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {record.driver && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PersonIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Driver</Typography>
                      <Typography variant="body1">
                        {record.driver?.user?.firstName} {record.driver?.user?.lastName}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                  <DescriptionIcon color="action" fontSize="small" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography variant="body1">
                      {record.description || 'No description provided'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Cost & Resolution Information */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Cost & Resolution
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AttachMoneyIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Repair Cost</Typography>
                    <Typography variant="body1" fontWeight="bold" color={record.repairCost > 0 ? 'error' : 'text.primary'}>
                      {formatCurrency(record.repairCost)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  {record.resolved ? (
                    <>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Chip label="RESOLVED" color="success" size="small" />
                      </Box>
                    </>
                  ) : (
                    <>
                      <WarningIcon color="warning" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Chip label="PENDING" color="warning" size="small" />
                      </Box>
                    </>
                  )}
                </Box>
              </Grid>
              {record.downtimeDays !== undefined && record.downtimeDays !== null && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CalendarTodayIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Downtime</Typography>
                      <Typography variant="body1">
                        {record.downtimeDays} days
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {record.resolvedDate && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CheckCircleIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Resolution Date</Typography>
                      <Typography variant="body1">
                        {formatDate(record.resolvedDate)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* File Attachments */}
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              File Attachments ({files.length})
            </Typography>
            
            {files.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No files attached to this incident record.
              </Typography>
            ) : (
              <List sx={{ mt: 2 }}>
                {files.map((file: any, index: number) => (
                  <ListItem
                    key={file.id || index}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:last-child': { mb: 0 },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon>
                      <AttachFileIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.file?.name || 'Unnamed file'}
                      secondary={formatFileSize(file.file?.size)}
                    />
                    <ListItemSecondaryAction>
                      {file.file?.mimeType?.startsWith('image/') && (
                        <IconButton
                          edge="end"
                          aria-label="view"
                          onClick={() => handleViewImage(file)}
                          sx={{ mr: 1 }}
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      )}
                      <IconButton
                        edge="end"
                        aria-label="download"
                        onClick={() => handleDownloadFile(file)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <IncidentDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        equipmentId={equipmentId}
        recordId={recordId}
        isEdit={true}
      />
    </>
  );
};